import React, { useState, useEffect, useCallback } from 'react';
import { 
  Decision, 
  DecisionType, 
  RiskLevel, 
  DECISION_TYPES, 
  INFORMATION_SOURCES,
  ValidationError
} from '../../types/audit-trail';
import { useDecisions } from '../../hooks/useDecisions';
import { EvidenceUploader } from './EvidenceUploader';
import { cn, isMobile, formatFileSize } from '../../lib/utils';

interface DecisionFormProps {
  initialDecision?: Partial<Decision>;
  onSave?: (decision: Decision) => void;
  onCancel?: () => void;
  quickMode?: boolean;
  className?: string;
}

interface Step {
  id: string;
  title: string;
  description: string;
  icon: string;
  completed: boolean;
  current: boolean;
}

interface FormData {
  title: string;
  type: DecisionType | '';
  decisionMade: string;
  rationale: string;
  riskLevel: RiskLevel | '';
  informationSources: string[];
  stakeholders: string[];
  alternatives: string;
  tags: string[];
  priority: 'low' | 'normal' | 'high' | 'urgent';
  location?: { lat: number; lng: number; address?: string };
}

export const DecisionForm: React.FC<DecisionFormProps> = ({
  initialDecision,
  onSave,
  onCancel,
  quickMode = false,
  className = ''
}) => {
  const { createDecision, updateDecision, validateDecision } = useDecisions();
  
  const [isSwipeView, setIsSwipeView] = useState(isMobile());
  const [currentStep, setCurrentStep] = useState(0);
  const [evidence, setEvidence] = useState(initialDecision?.evidence || []);
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState<FormData>({
    title: '',
    type: '',
    decisionMade: '',
    rationale: '',
    riskLevel: '',
    informationSources: [],
    stakeholders: [],
    alternatives: '',
    tags: [],
    priority: 'normal',
    location: undefined
  });
  
  // Multi-step form steps
  const steps: Step[] = [
    {
      id: 'basic',
      title: 'Basic Info',
      description: 'Decision title and type',
      icon: '📋',
      completed: false,
      current: currentStep === 0
    },
    {
      id: 'details',
      title: 'Decision Details',
      description: 'What and why',
      icon: '📝',
      completed: false,
      current: currentStep === 1
    },
    {
      id: 'risk',
      title: 'Risk Assessment',
      description: 'Evaluate risk level',
      icon: '⚠️',
      completed: false,
      current: currentStep === 2
    },
    {
      id: 'sources',
      title: 'Sources & Stakeholders',
      description: 'Information and consultation',
      icon: '👥',
      completed: false,
      current: currentStep === 3
    },
    {
      id: 'evidence',
      title: 'Evidence',
      description: 'Attach supporting files',
      icon: '📎',
      completed: false,
      current: currentStep === 4
    },
    {
      id: 'review',
      title: 'Review',
      description: 'Final review and submit',
      icon: '✅',
      completed: false,
      current: currentStep === 5
    }
  ];

  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [currentStakeholder, setCurrentStakeholder] = useState('');
  const [currentTag, setCurrentTag] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const [completionPercentage, setCompletionPercentage] = useState(0);

  // Auto-save functionality
  const [autoSaveTimeout, setAutoSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Handle window resize for mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsSwipeView(isMobile());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize form with existing decision
  useEffect(() => {
    if (initialDecision) {
      setFormData({
        title: initialDecision.title || '',
        type: initialDecision.type || '',
        decisionMade: initialDecision.decisionMade || '',
        rationale: initialDecision.rationale || '',
        riskLevel: initialDecision.riskLevel || '',
        informationSources: initialDecision.informationSources || [],
        stakeholders: initialDecision.stakeholders || [],
        alternatives: initialDecision.alternatives || '',
        tags: initialDecision.tags || [],
        priority: initialDecision.priority || 'normal',
        location: initialDecision.location
      });
      setEvidence(initialDecision.evidence || []);
    }
  }, [initialDecision?.id, initialDecision]);
  
  // Calculate form completion percentage
  useEffect(() => {
    const requiredFields = ['title', 'type', 'decisionMade', 'rationale', 'riskLevel'];
    const filledFields = requiredFields.filter(field => {
      const value = formData[field as keyof FormData];
      return value && value.toString().trim().length > 0;
    });
    
    let percentage = (filledFields.length / requiredFields.length) * 80; // 80% for required fields
    
    // Add bonus for optional fields
    if (formData.informationSources.length > 0) percentage += 5;
    if (formData.stakeholders.length > 0) percentage += 5;
    if (formData.alternatives.trim().length > 0) percentage += 5;
    if (evidence.length > 0) percentage += 5;
    
    setCompletionPercentage(Math.min(100, percentage));
  }, [formData, evidence]);

  // Validate form on changes
  useEffect(() => {
    if (isDirty) {
      const errors = validateDecision({
        ...formData,
        evidence: [], // Will be handled separately
        id: initialDecision?.id || '',
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: 'current-user', // Get from auth context
        createdByName: 'Current User',
        status: 'draft',
        syncStatus: 'pending',
        signature: '',
        deviceId: '',
        editHistory: []
      } as Decision);
      
      setValidationErrors(errors);
    }
  }, [formData, isDirty, validateDecision]);

  // Auto-save draft
  useEffect(() => {
    if (isDirty && !quickMode) {
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }

      const timeout = setTimeout(() => {
        saveDraft();
      }, 30000); // Auto-save every 30 seconds

      setAutoSaveTimeout(timeout);

      return () => {
        if (timeout) clearTimeout(timeout);
      };
    }
  }, [formData, isDirty, quickMode, autoSaveTimeout]);

  const handleInputChange = useCallback((
    field: keyof FormData, 
    value: any
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  }, []);

  const handleInfoSourceToggle = useCallback((source: string) => {
    setFormData(prev => ({
      ...prev,
      informationSources: prev.informationSources.includes(source)
        ? prev.informationSources.filter(s => s !== source)
        : [...prev.informationSources, source]
    }));
    setIsDirty(true);
  }, []);

  const handleAddStakeholder = useCallback(() => {
    if (currentStakeholder.trim() && !formData.stakeholders.includes(currentStakeholder.trim())) {
      setFormData(prev => ({
        ...prev,
        stakeholders: [...prev.stakeholders, currentStakeholder.trim()]
      }));
      setCurrentStakeholder('');
      setIsDirty(true);
    }
  }, [currentStakeholder, formData.stakeholders]);

  const handleRemoveStakeholder = useCallback((stakeholder: string) => {
    setFormData(prev => ({
      ...prev,
      stakeholders: prev.stakeholders.filter(s => s !== stakeholder)
    }));
    setIsDirty(true);
  }, []);

  const handleAddTag = useCallback(() => {
    if (currentTag.trim() && !formData.tags.includes(currentTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, currentTag.trim()]
      }));
      setCurrentTag('');
      setIsDirty(true);
    }
  }, [currentTag, formData.tags]);

  const handleRemoveTag = useCallback((tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
    setIsDirty(true);
  }, []);

  const getCurrentLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser');
      return;
    }

    setLocationLoading(true);
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        });
      });

      const location = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy
      };

      // Try to get address from coordinates
      try {
        const response = await fetch(
          `https://api.openstreetmap.org/reverse?format=json&lat=${location.lat}&lon=${location.lng}`
        );
        const data = await response.json();
        (location as any).address = data.display_name;
      } catch (e) {
        console.warn('Could not get address for location');
      }

      setFormData(prev => ({ ...prev, location }));
      setIsDirty(true);
    } catch (error: any) {
      console.error('Error getting location:', error);
      alert('Could not get current location. Please check your browser permissions.');
    } finally {
      setLocationLoading(false);
    }
  }, []);

  const saveDraft = useCallback(async () => {
    try {
      const decision = {
        ...formData,
        type: (formData.type || 'other') as DecisionType,
        riskLevel: (formData.riskLevel || 'medium') as RiskLevel,
        evidence: [],
        createdBy: 'current-user', // Get from auth context
        createdByName: 'Current User',
        status: 'draft' as const,
        syncStatus: 'pending' as const,
        deviceId: 'browser-' + new Date().getTime(),
        editHistory: []
      };

      if (initialDecision?.id) {
        await updateDecision(initialDecision.id, decision);
      } else {
        await createDecision(decision);
      }
    } catch (error) {
      console.error('Failed to save draft:', error);
    }
  }, [formData, initialDecision, updateDecision, createDecision]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowValidation(true);

    if (validationErrors.length > 0) {
      // Scroll to first error
      const firstErrorField = validationErrors[0].field;
      const element = document.querySelector(`[data-field="${firstErrorField}"]`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setIsSubmitting(true);
    try {
      const decision = {
        ...formData,
        type: (formData.type || 'other') as DecisionType,
        riskLevel: (formData.riskLevel || 'medium') as RiskLevel,
        evidence: initialDecision?.evidence || [],
        createdBy: 'current-user', // Get from auth context
        createdByName: 'Current User',
        status: 'submitted' as const,
        syncStatus: 'pending' as const,
        deviceId: initialDecision?.deviceId || 'browser-' + new Date().getTime(),
        editHistory: initialDecision?.editHistory || []
      };

      let savedDecision: Decision;
      if (initialDecision?.id) {
        savedDecision = await updateDecision(initialDecision.id, decision);
      } else {
        savedDecision = await createDecision(decision);
      }

      onSave?.(savedDecision);
    } catch (error: any) {
      console.error('Failed to save decision:', error);
      alert(`Failed to save decision: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFieldError = (field: string): ValidationError | undefined => {
    return validationErrors.find(error => error.field === field);
  };

  const isFieldValid = (field: string): boolean => {
    return !getFieldError(field) || !showValidation;
  };
  
  // Check if current step is valid
  const isStepValid = (stepIndex: number): boolean => {
    switch (stepIndex) {
      case 0: // Basic Info
        return formData.title.trim().length > 0 && formData.type.length > 0;
      case 1: // Decision Details
        return formData.decisionMade.trim().length > 0 && formData.rationale.trim().length > 0;
      case 2: // Risk Assessment
        return formData.riskLevel.length > 0;
      case 3: // Sources & Stakeholders
        return formData.informationSources.length > 0;
      case 4: // Evidence
        return true; // Evidence is optional
      case 5: // Review
        return validationErrors.length === 0;
      default:
        return false;
    }
  };
  
  const canProceedToNextStep = (): boolean => {
    return isStepValid(currentStep);
  };
  
  const nextStep = () => {
    if (canProceedToNextStep() && currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const goToStep = (stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setCurrentStep(stepIndex);
    }
  };

  // Mobile Step Navigation Component
  const MobileStepNavigation = () => (
    <div className="mobile-step-nav bg-white border-b border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-900">{steps[currentStep].title}</h2>
        <span className="text-sm text-gray-500">{currentStep + 1} of {steps.length}</span>
      </div>
      
      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
        <div 
          className="bg-orange-500 h-2 rounded-full transition-all duration-300" 
          style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
        />
      </div>
      
      <p className="text-sm text-gray-600">{steps[currentStep].description}</p>
    </div>
  );

  // Desktop Progress Stepper
  const DesktopStepper = () => (
    <div className="bg-white border-b border-gray-200 p-6">
      <div className="max-w-4xl mx-auto">
        <nav aria-label="Progress">
          <ol className="flex items-center">
            {steps.map((step, index) => (
              <li key={step.id} className={cn("relative", index !== steps.length - 1 && "pr-8 sm:pr-20")}>
                {index !== steps.length - 1 && (
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className={cn("h-0.5 w-full", 
                      index < currentStep ? "bg-orange-500" : "bg-gray-200"
                    )} />
                  </div>
                )}
                
                <button
                  onClick={() => goToStep(index)}
                  className={cn(
                    "relative w-8 h-8 flex items-center justify-center rounded-full text-sm font-medium transition-colors",
                    index < currentStep
                      ? "bg-orange-500 text-white"
                      : index === currentStep
                      ? "bg-orange-500 text-white"
                      : "bg-gray-200 text-gray-500 hover:bg-gray-300"
                  )}
                >
                  {index < currentStep ? (
                    <span>✓</span>
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </button>
                
                <div className="mt-2">
                  <p className={cn("text-xs font-medium",
                    index <= currentStep ? "text-orange-600" : "text-gray-500"
                  )}>
                    {step.title}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </nav>
        
        <div className="mt-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{steps[currentStep].title}</h2>
            <p className="text-sm text-gray-600">{steps[currentStep].description}</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500 mb-1">Form completion</div>
            <div className="text-lg font-semibold text-orange-600">{completionPercentage}%</div>
          </div>
        </div>
      </div>
    </div>
  );

  // Quick mode renders simplified form
  if (quickMode) {
    return (
      <div className={cn("decision-form quick-mode min-h-screen", className, isSwipeView && "pb-20")}>
        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-4 text-center">
          <h2 className="text-xl font-bold">🚨 Quick Decision Entry</h2>
          <p className="text-sm opacity-90">For emergency situations - complete details can be added later</p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-6">
          <div className="space-y-4">
            <div>
              <label htmlFor="quick-title" className="block text-sm font-medium text-gray-700 mb-2">
                What decision was made? *
              </label>
              <input
                id="quick-title"
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Brief description of the decision..."
                className={cn(
                  "w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent",
                  isFieldValid('title') ? "border-gray-300" : "border-red-300 bg-red-50"
                )}
                data-field="title"
                required
              />
              {!isFieldValid('title') && (
                <div className="mt-1 text-sm text-red-600">{getFieldError('title')?.message}</div>
              )}
            </div>

            <div>
              <label htmlFor="quick-rationale" className="block text-sm font-medium text-gray-700 mb-2">
                Why was this decision made? *
              </label>
              <textarea
                id="quick-rationale"
                value={formData.rationale}
                onChange={(e) => handleInputChange('rationale', e.target.value)}
                placeholder="Brief explanation of the reasoning..."
                rows={4}
                className={cn(
                  "w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none",
                  isFieldValid('rationale') ? "border-gray-300" : "border-red-300 bg-red-50"
                )}
                data-field="rationale"
                required
              />
              {!isFieldValid('rationale') && (
                <div className="mt-1 text-sm text-red-600">{getFieldError('rationale')?.message}</div>
              )}
            </div>

            <div>
              <label htmlFor="quick-risk" className="block text-sm font-medium text-gray-700 mb-2">
                Risk Level *
              </label>
              <select
                id="quick-risk"
                value={formData.riskLevel}
                onChange={(e) => handleInputChange('riskLevel', e.target.value as RiskLevel)}
                className={cn(
                  "w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent",
                  isFieldValid('riskLevel') ? "border-gray-300" : "border-red-300 bg-red-50"
                )}
                data-field="riskLevel"
                required
              >
                <option value="">Select risk level</option>
                <option value="low">🟢 Low Risk - Minimal impact if wrong</option>
                <option value="medium">🟡 Medium Risk - Moderate consequences possible</option>
                <option value="high">🔴 High Risk - Significant impact on operations/safety</option>
              </select>
              {!isFieldValid('riskLevel') && (
                <div className="mt-1 text-sm text-red-600">{getFieldError('riskLevel')?.message}</div>
              )}
            </div>
          </div>

          <div className="border-t pt-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <button 
                type="button" 
                onClick={onCancel} 
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={isSubmitting || !formData.title.trim() || !formData.rationale.trim() || !formData.riskLevel} 
                className="flex-1 px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-400 disabled:text-gray-100 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </span>
                ) : (
                  '💾 Save Quick Decision'
                )}
              </button>
            </div>
          </div>
          
          {isDirty && !isSubmitting && (
            <div className="text-center text-sm text-gray-500">
              • Unsaved changes
            </div>
          )}
        </form>
      </div>
    );
  }

  // Multi-step form render functions
  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Basic Information
        return (
          <div className="space-y-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                Decision Title *
              </label>
              <input
                id="title"
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Clear, descriptive title of the decision made"
                className={cn(
                  "w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent",
                  isFieldValid('title') ? "border-gray-300" : "border-red-300 bg-red-50"
                )}
                data-field="title"
                maxLength={200}
                required
              />
              <div className="mt-1 text-xs text-gray-500">
                A clear title helps with searching and audit reviews
              </div>
              {!isFieldValid('title') && (
                <div className="mt-1 text-sm text-red-600">{getFieldError('title')?.message}</div>
              )}
            </div>

            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-2">
                Decision Type *
              </label>
              <select
                id="type"
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value as DecisionType)}
                className={cn(
                  "w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent",
                  isFieldValid('type') ? "border-gray-300" : "border-red-300 bg-red-50"
                )}
                data-field="type"
                required
              >
                <option value="">Select decision type</option>
                {Object.entries(DECISION_TYPES).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              {!isFieldValid('type') && (
                <div className="mt-1 text-sm text-red-600">{getFieldError('type')?.message}</div>
              )}
            </div>

            <div>
              <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-2">
                Priority Level
              </label>
              <select
                id="priority"
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="low">Low Priority</option>
                <option value="normal">Normal Priority</option>
                <option value="high">High Priority</option>
                <option value="urgent">🚨 Urgent Priority</option>
              </select>
            </div>
          </div>
        );

      case 1: // Decision Details
        return (
          <div className="space-y-6">
            <div>
              <label htmlFor="decision-made" className="block text-sm font-medium text-gray-700 mb-2">
                What decision was made? *
              </label>
              <textarea
                id="decision-made"
                value={formData.decisionMade}
                onChange={(e) => handleInputChange('decisionMade', e.target.value)}
                placeholder="Describe exactly what was decided and what actions will be taken..."
                rows={isSwipeView ? 4 : 6}
                className={cn(
                  "w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none",
                  isFieldValid('decisionMade') ? "border-gray-300" : "border-red-300 bg-red-50"
                )}
                data-field="decisionMade"
                maxLength={2000}
                required
              />
              <div className="mt-1 text-xs text-gray-500">
                Be specific about actions taken and outcomes expected
              </div>
              {!isFieldValid('decisionMade') && (
                <div className="mt-1 text-sm text-red-600">{getFieldError('decisionMade')?.message}</div>
              )}
            </div>

            <div>
              <label htmlFor="rationale" className="block text-sm font-medium text-gray-700 mb-2">
                Why was this decision made? *
              </label>
              <textarea
                id="rationale"
                value={formData.rationale}
                onChange={(e) => handleInputChange('rationale', e.target.value)}
                placeholder="Explain the reasoning, context, and factors that led to this decision..."
                rows={isSwipeView ? 5 : 7}
                className={cn(
                  "w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none",
                  isFieldValid('rationale') ? "border-gray-300" : "border-red-300 bg-red-50"
                )}
                data-field="rationale"
                maxLength={3000}
                required
              />
              <div className="mt-1 text-xs text-gray-500">
                Detailed rationale protects you during reviews and audits
              </div>
              {!isFieldValid('rationale') && (
                <div className="mt-1 text-sm text-red-600">{getFieldError('rationale')?.message}</div>
              )}
            </div>

            <div>
              <label htmlFor="alternatives" className="block text-sm font-medium text-gray-700 mb-2">
                Alternatives Considered
              </label>
              <textarea
                id="alternatives"
                value={formData.alternatives}
                onChange={(e) => handleInputChange('alternatives', e.target.value)}
                placeholder="What other options were considered and why were they not chosen?"
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                data-field="alternatives"
                maxLength={2000}
              />
              <div className="mt-1 text-xs text-gray-500">
                Documenting alternatives shows thorough decision-making
              </div>
            </div>
          </div>
        );

      case 2: // Risk Assessment
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Risk Level *
              </label>
              <div className="space-y-3">
                {[
                  { value: 'low', label: '🟢 Low Risk', desc: 'Minimal impact if wrong' },
                  { value: 'medium', label: '🟡 Medium Risk', desc: 'Moderate consequences possible' },
                  { value: 'high', label: '🔴 High Risk', desc: 'Significant impact on operations/safety' }
                ].map(({ value, label, desc }) => (
                  <label key={value} className={cn(
                    "flex items-start p-4 border rounded-lg cursor-pointer transition-colors",
                    formData.riskLevel === value 
                      ? "border-orange-500 bg-orange-50" 
                      : "border-gray-300 hover:bg-gray-50"
                  )}>
                    <input
                      type="radio"
                      name="riskLevel"
                      value={value}
                      checked={formData.riskLevel === value}
                      onChange={(e) => handleInputChange('riskLevel', e.target.value as RiskLevel)}
                      className="mt-1 text-orange-500 focus:ring-orange-500"
                      required
                    />
                    <div className="ml-3">
                      <div className="font-medium text-gray-900">{label}</div>
                      <div className="text-sm text-gray-600">{desc}</div>
                    </div>
                  </label>
                ))}
              </div>
              {!isFieldValid('riskLevel') && (
                <div className="mt-1 text-sm text-red-600">{getFieldError('riskLevel')?.message}</div>
              )}
            </div>
          </div>
        );

      case 3: // Sources & Stakeholders
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Information Sources *
              </label>
              <p className="text-sm text-gray-600 mb-4">Select all information sources that informed this decision</p>
              
              <div className={cn(
                "grid gap-3",
                isSwipeView ? "grid-cols-1" : "grid-cols-2"
              )}>
                {INFORMATION_SOURCES.map(source => (
                  <label key={source} className={cn(
                    "flex items-center p-3 border rounded-lg cursor-pointer transition-colors",
                    formData.informationSources.includes(source)
                      ? "border-orange-500 bg-orange-50"
                      : "border-gray-300 hover:bg-gray-50"
                  )}>
                    <input
                      type="checkbox"
                      checked={formData.informationSources.includes(source)}
                      onChange={() => handleInfoSourceToggle(source)}
                      className="text-orange-500 focus:ring-orange-500"
                    />
                    <span className="ml-3 text-sm text-gray-900">{source}</span>
                  </label>
                ))}
              </div>
              {!isFieldValid('informationSources') && (
                <div className="mt-1 text-sm text-red-600">{getFieldError('informationSources')?.message}</div>
              )}
            </div>

            <div>
              <label htmlFor="stakeholder-input" className="block text-sm font-medium text-gray-700 mb-2">
                Stakeholders Consulted
              </label>
              <div className="flex gap-2">
                <input
                  id="stakeholder-input"
                  type="text"
                  value={currentStakeholder}
                  onChange={(e) => setCurrentStakeholder(e.target.value)}
                  placeholder="Name, role, or organization"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddStakeholder())}
                />
                <button
                  type="button"
                  onClick={handleAddStakeholder}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                  Add
                </button>
              </div>

              {formData.stakeholders.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {formData.stakeholders.map(stakeholder => (
                    <span key={stakeholder} className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">
                      {stakeholder}
                      <button
                        type="button"
                        onClick={() => handleRemoveStakeholder(stakeholder)}
                        className="ml-2 text-gray-500 hover:text-gray-700"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                📍 Location (Optional)
              </label>
              
              <button
                type="button"
                onClick={getCurrentLocation}
                disabled={locationLoading}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:bg-gray-400 disabled:text-gray-100 transition-colors"
              >
                {locationLoading ? '📍 Getting location...' : '📍 Capture Current Location'}
              </button>
              
              {formData.location && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">📍 Location captured:</p>
                  <p className="text-sm text-gray-600">
                    Coordinates: {formData.location.lat.toFixed(6)}, {formData.location.lng.toFixed(6)}
                  </p>
                  {formData.location.address && (
                    <p className="text-sm text-gray-600">Address: {formData.location.address}</p>
                  )}
                  <button
                    type="button"
                    onClick={() => handleInputChange('location', undefined)}
                    className="mt-2 text-sm text-red-600 hover:text-red-800"
                  >
                    Remove location
                  </button>
                </div>
              )}
            </div>

            {/* Tags */}
            <div>
              <label htmlFor="tag-input" className="block text-sm font-medium text-gray-700 mb-2">
                Tags (Optional)
              </label>
              <div className="flex gap-2">
                <input
                  id="tag-input"
                  type="text"
                  value={currentTag}
                  onChange={(e) => setCurrentTag(e.target.value)}
                  placeholder="e.g., urgent, food-security, coordination"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                >
                  Add
                </button>
              </div>
              <div className="mt-1 text-xs text-gray-500">
                Tags help with organization and searching
              </div>

              {formData.tags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {formData.tags.map(tag => (
                    <span key={tag} className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-2 text-gray-500 hover:text-gray-700"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 4: // Evidence
        return (
          <div className="space-y-6">
            <EvidenceUploader
              evidence={evidence}
              onEvidenceChange={setEvidence}
              decisionId={initialDecision?.id}
              className={isSwipeView ? "mobile-evidence" : ""}
            />
          </div>
        );

      case 5: // Review
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">📋 Review Your Decision</h3>
              <p className="text-sm text-blue-700">Please review all information before submitting. You can go back to make changes.</p>
            </div>

            <div className="space-y-4">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Basic Information</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Title:</strong> {formData.title}</p>
                  <p><strong>Type:</strong> {DECISION_TYPES[formData.type as DecisionType] || formData.type}</p>
                  <p><strong>Priority:</strong> {formData.priority}</p>
                  <p><strong>Risk Level:</strong> {formData.riskLevel}</p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Decision Details</h4>
                <div className="text-sm text-gray-600 space-y-2">
                  <div>
                    <p className="font-medium">Decision Made:</p>
                    <p>{formData.decisionMade}</p>
                  </div>
                  <div>
                    <p className="font-medium">Rationale:</p>
                    <p>{formData.rationale}</p>
                  </div>
                  {formData.alternatives && (
                    <div>
                      <p className="font-medium">Alternatives Considered:</p>
                      <p>{formData.alternatives}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Sources & Stakeholders</h4>
                <div className="text-sm text-gray-600 space-y-2">
                  <div>
                    <p className="font-medium">Information Sources ({formData.informationSources.length}):</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {formData.informationSources.map(source => (
                        <span key={source} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">{source}</span>
                      ))}
                    </div>
                  </div>
                  {formData.stakeholders.length > 0 && (
                    <div>
                      <p className="font-medium">Stakeholders ({formData.stakeholders.length}):</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {formData.stakeholders.map(stakeholder => (
                          <span key={stakeholder} className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">{stakeholder}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {formData.tags.length > 0 && (
                    <div>
                      <p className="font-medium">Tags ({formData.tags.length}):</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {formData.tags.map(tag => (
                          <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {evidence.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Evidence ({evidence.length} files)</h4>
                  <div className="text-sm text-gray-600">
                    <p>Total size: {formatFileSize(evidence.reduce((total, e) => total + e.size, 0))}</p>
                  </div>
                </div>
              )}
            </div>

            {validationErrors.length > 0 && showValidation && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-900 mb-2">⚠️ Please address the following issues:</h4>
                <ul className="list-disc list-inside text-sm text-red-700">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error.message}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      default:
        return <div>Invalid step</div>;
    }
  };

  // Mobile bottom navigation
  const MobileBottomNavigation = () => (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-50">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={prevStep}
          disabled={currentStep === 0}
          className={cn(
            "px-4 py-2 rounded-lg transition-colors",
            currentStep === 0
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          )}
        >
          ← Previous
        </button>
        
        <div className="flex items-center gap-2">
          {currentStep < steps.length - 1 ? (
            <button
              type="button"
              onClick={nextStep}
              disabled={!canProceedToNextStep()}
              className={cn(
                "px-6 py-2 rounded-lg transition-colors",
                canProceedToNextStep()
                  ? "bg-orange-500 text-white hover:bg-orange-600"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              )}
            >
              Next →
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={saveDraft}
                disabled={isSubmitting}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:bg-gray-400 disabled:text-gray-100"
              >
                💾 Draft
              </button>
              <button
                type="submit"
                disabled={isSubmitting || validationErrors.length > 0}
                className={cn(
                  "px-6 py-2 rounded-lg transition-colors",
                  isSubmitting || validationErrors.length > 0
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-green-500 text-white hover:bg-green-600"
                )}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </span>
                ) : (
                  '✅ Submit'
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Desktop form actions
  const DesktopFormActions = () => (
    <div className="bg-white border-t border-gray-200 p-6 sticky bottom-0">
      <div className="max-w-4xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          
          {currentStep > 0 && (
            <button
              type="button"
              onClick={prevStep}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              ← Previous
            </button>
          )}
        </div>

        <div className="flex items-center gap-4">
          {currentStep < steps.length - 1 ? (
            <button
              type="button"
              onClick={nextStep}
              disabled={!canProceedToNextStep()}
              className={cn(
                "px-6 py-2 rounded-lg transition-colors",
                canProceedToNextStep()
                  ? "bg-orange-500 text-white hover:bg-orange-600"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              )}
            >
              Next →
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={saveDraft}
                disabled={isSubmitting}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:bg-gray-400 disabled:text-gray-100 transition-colors"
              >
                💾 Save Draft
              </button>
              <button
                type="submit"
                disabled={isSubmitting || validationErrors.length > 0}
                className={cn(
                  "px-6 py-2 rounded-lg transition-colors",
                  isSubmitting || validationErrors.length > 0
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-green-500 text-white hover:bg-green-600"
                )}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </span>
                ) : (
                  '✅ Submit Decision'
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // Full form
  return (
    <div className={cn("decision-form min-h-screen", className, isSwipeView && "pb-20")}>
      <form onSubmit={handleSubmit}>
        {/* Progress indicator */}
        {isSwipeView ? <MobileStepNavigation /> : <DesktopStepper />}

        {/* Form content */}
        <div className={cn("form-content", isSwipeView ? "p-4" : "max-w-4xl mx-auto p-6")}>
          {renderStepContent()}
        </div>

        {/* Form actions */}
        {isSwipeView ? <MobileBottomNavigation /> : <DesktopFormActions />}
        
        {/* Save status */}
        {isDirty && !isSubmitting && !isSwipeView && (
          <div className="fixed bottom-4 right-4 bg-orange-100 text-orange-700 px-3 py-2 rounded-lg shadow-lg text-sm">
            • Unsaved changes
          </div>
        )}
      </form>
    </div>
  );
};